import React, { useContext, useState } from "react";
import {
  Typography,
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { ReactComponent as PenIcon } from "../../../images/penIcon.svg";
import CommonAccordion from "../../custom-components/CommonAccordion";
import CommonDownload from "../../custom-components/CommonDownload";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { ReactComponent as BusinessProcessGuideIcon } from "../../../images/businessProcessGuideIcon.svg";
import { LoadingModalContext } from "../../main-pages/ServicePage";
import {
  userStoryToUseCase,
  businessProcessGuide,
} from "../../../services/webServices";
import { useLocation, useNavigate } from "react-router-dom";
import { flattenArray, flattenObject } from "../../../utils/dataFlatteners";
import { ReactComponent as GenerateButton } from "../../../images/generateButton.svg";
import {
  UserStoryFlowDataContext,
  UserStoryLoaderStateContext,
} from "../../main-pages/UserStoryMainPage";
import MessagePopup from "../../popup-components/MessagePopup";
import { ForkRight } from "@mui/icons-material";
import { handleProgressBar } from "../../custom-components/ProgressBar/ProgressBarWithAPI";

const RefinedUserStoryComponent = () => {
  const [openDownload, setOpenDownload] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [activeThumb, setActiveThumb] = useState(null);
  const [businessProcessData, setBusinessProcessData] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [sessionExpiredValue, setSessionExpiredValue] = useState(false);

  const { setUserStoryLoaderState } = useContext(UserStoryLoaderStateContext);
  const { useCase, setUseCase } = useContext(UserStoryFlowDataContext);

  const refinedUserStory = location.state["refinedUserStory"];
  const editableFields = location.state.useCase || {};
  const useCaseData = {};
  Object.entries(editableFields).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      useCaseData[key] = flattenArray(value);
    } else if (!!value && typeof value === "object") {
      useCaseData[key] = flattenObject(value);
    } else {
      useCaseData[key] = value;
    }
  });

  const defaultRefinedUserStoryExpanded =
    Object.keys(refinedUserStory).length > 0 &&
    Object.keys(useCase).length === 0;

  const flattenedRefinedUserStory = {};
  Object.entries(refinedUserStory).map(([key, value]) => {
    if (Array.isArray(value)) {
      return (flattenedRefinedUserStory[key] = flattenArray(value));
    }
    if (!!value && typeof value === "object") {
      return (flattenedRefinedUserStory[key] = flattenObject(value));
    }
    return (flattenedRefinedUserStory[key] = value);
  });

  const { setLoadingState } = useContext(LoadingModalContext);

  const handleThumbClick = (thumbType) => {
    setActiveThumb((prev) => (prev === thumbType ? null : thumbType));
  };

  const handleCopyClick = () => {
    navigator.clipboard.writeText("Content to be copied");
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleBusinessProcessGuide = async () => {
    let progressHandler;
    try {
      setUseCase({});
      setUserStoryLoaderState("Use Case");
      progressHandler = handleProgressBar({
        setLoadingState,
        totalDurationMs: 22000,
      });
      progressHandler.start("Generating Use Case");
      const generatedUseCase = await userStoryToUseCase({
        userStory: flattenedRefinedUserStory,
      });

      if (!generatedUseCase?.data) {
        console.warn("No data returned for generatedUseCase");
        return;
      }
      progressHandler.updateMessage("Generating Business Process Guide");
      const response = await businessProcessGuide(generatedUseCase.data);
      setBusinessProcessData(response.data);

      await progressHandler.finishAndClose();

      navigate("/dashboard/userstory/output/businessProcessGuide", {
        state: {
          ...location.state,
          useCase: generatedUseCase.data,
          businessProcessData: response.data,
        },
      });
    } catch (error) {
      const errorMsg =
        error?.response?.data?.Error || "An error occurred. Please try again.";
      setPopupMessage(errorMsg);
      setPopupOpen(true);

      if (error.response?.data?.Error === "Your session has expired.") {
        setSessionExpiredValue(true);
      }
      setLoadingState({ open: false, message: "", progress: null });
    } finally {
      if (progressHandler) {
        progressHandler.stop();
      }
      setLoadingState({ open: false, message: "", progress: null });
    }
  };

  const EditableField = ({ label, initialValue, textProps }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(initialValue);
    const [tempValue, setTempValue] = useState(initialValue);

    const handleSave = (label) => {
      setValue(tempValue); // Save the edited value
      const updatedRefinedUserStory = {
        ...refinedUserStory,
        [label]: tempValue,
      };
      navigate("/dashboard/userstory/output", {
        state: { ...location.state, refinedUserStory: updatedRefinedUserStory },
        replace: true,
      });
      setIsEditing(false); // Exit edit mode
    };

    const handleCancel = () => {
      setTempValue(value); // Revert to original value
      setIsEditing(false); // Exit edit mode
    };

    return (
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "100%", // Ensures it doesn't exceed screen width
          overflow: "hidden",
          "&:hover .edit-icon": { visibility: "visible" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            // marginBottom: 1,
            justifyContent: "space-between",
            paddingRight: 3, // Optional: adds spacing from the right edge
          }}
        >
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{
              fontSize: "14px",
              marginLeft: 1,
              fontWeight: "bold",
              color: "#000",
            }}
          >
            {label}
          </Typography>

          {!isEditing && (
            <IconButton
              onClick={() => setIsEditing(true)}
              className="edit-icon"
              size="small"
              sx={{ p: 0.5 }}
            >
              <PenIcon style={{ width: 18, height: 18 }} />
            </IconButton>
          )}
        </Box>

        {isEditing ? (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              sx={{ marginTop: 1, marginRight: 1 }}
              inputProps={{ style: { fontSize: "14px" } }}
            />
            <Box>
              {/* Save Button */}
              <Button
                onClick={() => handleSave(label)}
                variant="text"
                size="small"
                sx={{ marginRight: 1, color: "#49A3FF", textTransform: "none" }}
              >
                Save
              </Button>
              {/* Cancel Button */}
              <Button
                onClick={handleCancel}
                variant="outline"
                size="small"
                sx={{ marginRight: 1, color: "#49A3FF", textTransform: "none" }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              "&:hover": { backgroundColor: "white" },
              mr: 2,
            }}
          >
            {/* <Typography
              variant="body1"
              {...textProps}
              dangerouslySetInnerHTML={{
                __html: value.toString().replace(/\n/g, "<br>"),
              }}
            /> */}
            {/* <Typography variant="body1" {...textProps}>
              {value.toString()}
            </Typography> */}
            <Typography variant="body1" {...textProps}>
              {value
                .toString()
                .split("\n")
                .map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const accordionContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        width: "100%",
      }}
    >
      <Box sx={{ width: "100%" }}>
        <EditableField
          label="Title"
          initialValue={flattenedRefinedUserStory["Title"]}
          textProps={{
            sx: {
              marginBottom: 2,
              fontSize: "14px",
              marginLeft: 1,
              paddingRight: 5,
            },
          }}
        />
      </Box>
      <Box sx={{ width: "100%" }}>
        <EditableField
          label="Description"
          initialValue={flattenedRefinedUserStory["Description"]}
          textProps={{
            sx: {
              marginBottom: 2,
              fontSize: "14px",
              marginLeft: 1,
              paddingRight: 5,
            },
          }}
        />
      </Box>
      <Box sx={{ width: "100%" }}>
        <EditableField
          label="Acceptance Criteria"
          initialValue={flattenedRefinedUserStory["Acceptance Criteria"]}
          textProps={{
            sx: {
              marginBottom: 2,
              fontSize: "14px",
              marginLeft: 1,
              paddingRight: 5,
            },
          }}
        />
      </Box>
      <Box sx={{ width: "100%" }}>
        <EditableField
          label="Additional Information"
          initialValue={flattenedRefinedUserStory["Additional Information"]}
          textProps={{
            sx: {
              marginBottom: 2,
              fontSize: "14px",
              marginLeft: 1,
              paddingRight: 5,
            },
          }}
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", marginTop: 2, mb: 2 }}>
        {/* Thumbs Up Button with Tooltip */}
        <Tooltip title="Like" arrow>
          <Box
            onClick={() => handleThumbClick("like")}
            sx={{
              cursor: "pointer",
              marginRight: 2, // Margin between the buttons
              "&:hover": {
                color: "#49A3FF", // Change color on hover
              },
            }}
          >
            {activeThumb === "like" ? (
              <ThumbUpIcon sx={{ fontSize: "24px" }} /> // Filled thumbs up when clicked
            ) : (
              <ThumbUpOutlinedIcon sx={{ fontSize: "24px" }} /> // Outlined thumbs up by default
            )}
          </Box>
        </Tooltip>

        {/* Thumbs Down Button with Tooltip */}
        <Tooltip title="Dislike" arrow>
          <Box
            onClick={() => handleThumbClick("dislike")}
            sx={{
              cursor: "pointer",
              marginRight: 2, // Margin between the buttons
              "&:hover": {
                color: "#FF4842", // Change color on hover
              },
            }}
          >
            {activeThumb === "dislike" ? (
              <ThumbDownIcon sx={{ fontSize: "24px" }} /> // Filled thumbs down when clicked
            ) : (
              <ThumbDownOutlinedIcon sx={{ fontSize: "24px" }} /> // Outlined thumbs down by default
            )}
          </Box>
        </Tooltip>

        {/* Copy Button with Tooltip */}
        <Tooltip title={isCopied ? "Copied!" : "Copy"} arrow>
          <Box
            onClick={handleCopyClick}
            sx={{
              cursor: "pointer",
              "&:hover": {
                color: "#49A3FF", // Change color on hover
              },
            }}
          >
            {isCopied ? (
              <ContentCopyIcon sx={{ fontSize: "24px" }} /> // Filled copy icon when copied
            ) : (
              <ContentCopyOutlinedIcon sx={{ fontSize: "24px" }} /> // Outlined copy icon by default
            )}
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );

  const buttonsgroup = [
    {
      label: "Download",
      variant: "text",
      startIcon: <FileDownloadOutlinedIcon />,
      onClick: () => setOpenDownload(true), // Open CommonDownload dialog
      sx: { textTransform: "none" },
    },
    {
      label: "Business Process Guide",
      onClick: handleBusinessProcessGuide,
      // startIcon: <BusinessProcessGuideIcon />,
      variant: "contained",
      sx: { fontSize: "14px", textTransform: "none", float: "right" },
    },
  ];

  return (
    <Box sx={{}}>
      <CommonAccordion
        summary={
          <Typography variant="h6" sx={{ fontSize: "16px" }}>
            Refined User Story
          </Typography>
        }
        details={accordionContent}
        buttons={buttonsgroup}
        type={"output"}
        fullScreenMode={true}
        defaultExpanded={true}
      />
      <CommonDownload
        open={openDownload}
        onClose={() => setOpenDownload(false)}
        serviceName="Refined User Story"
        wordData={[
          {
            heading: "Refined User Story",
            type: "subParagraphs",
            data: refinedUserStory,
          },
        ]}
        serviceResponse={[
          {
            heading: "Refined User Story",
            type: "subParagraphs",
            data: flattenedRefinedUserStory,
          },
        ]}
        documents={["docx", "pdf"]}
      />
      <MessagePopup
        modalOpen={popupOpen}
        okayHandler={() => setPopupOpen(false)}
        message={popupMessage}
      />
    </Box>
  );
};

export default RefinedUserStoryComponent;
